const STORAGE_KEY = 'ritual-color-mode-v1';
const PREFERENCES = ['light', 'dark', 'system'];
const systemPreference = window.matchMedia('(prefers-color-scheme: dark)');

function isPreference(value) {
  return PREFERENCES.includes(value);
}

function getStoredPreference() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(value) ? value : 'light';
  } catch (_) {
    return 'light';
  }
}

function resolveMode(preference) {
  if (preference === 'system') return systemPreference.matches ? 'dark' : 'light';
  return preference;
}

function updateThemeColor() {
  const themeColor = getComputedStyle(document.documentElement).getPropertyValue('--mode-color-background').trim();
  if (themeColor) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
}

function applyPreference(preference, { persist = true, announce = true } = {}) {
  const safePreference = isPreference(preference) ? preference : 'light';
  const resolvedMode = resolveMode(safePreference);
  const root = document.documentElement;

  root.dataset.colorPreference = safePreference;
  root.dataset.colorMode = resolvedMode;
  root.style.colorScheme = resolvedMode;

  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEY, safePreference);
    } catch (_) {
      // The selected mode still applies for the current page.
    }
  }

  requestAnimationFrame(updateThemeColor);

  if (announce) {
    document.dispatchEvent(
      new CustomEvent('ritual:color-mode-change', {
        detail: { preference: safePreference, resolvedMode },
      })
    );
  }
}

class ColorModeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!(this.button instanceof HTMLButtonElement)) return;

    // Listen on the custom element so the control survives header morphs that
    // replace the inner button after this component has been connected.
    this.addEventListener('click', this.handleClick);
    document.addEventListener('ritual:color-mode-change', this.handleChange);
    this.updateAccessibleLabel();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('ritual:color-mode-change', this.handleChange);
  }

  handleClick = () => {
    const currentPreference = document.documentElement.dataset.colorPreference || getStoredPreference();
    const currentIndex = PREFERENCES.indexOf(currentPreference);
    applyPreference(PREFERENCES[(currentIndex + 1) % PREFERENCES.length]);
  };

  handleChange = () => this.updateAccessibleLabel();

  updateAccessibleLabel() {
    this.button = this.querySelector('button');
    if (!(this.button instanceof HTMLButtonElement)) return;

    const preference = document.documentElement.dataset.colorPreference || 'light';
    const currentIndex = Math.max(0, PREFERENCES.indexOf(preference));
    const nextPreference = PREFERENCES[(currentIndex + 1) % PREFERENCES.length];
    const label = this.dataset[`name${preference[0].toUpperCase()}${preference.slice(1)}`];
    const nextLabel = this.dataset[`name${nextPreference[0].toUpperCase()}${nextPreference.slice(1)}`];
    const template = this.dataset.labelTemplate || '__current__. __next__.';

    this.button.setAttribute(
      'aria-label',
      template.replace('__current__', label || preference).replace('__next__', nextLabel || nextPreference)
    );
    this.button.setAttribute('title', label || preference);
  }
}

if (!customElements.get('color-mode-toggle')) {
  customElements.define('color-mode-toggle', ColorModeToggle);
}

window.addEventListener('storage', (event) => {
  if (event.storageArea && event.storageArea !== window.localStorage) return;
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  applyPreference(isPreference(event.newValue) ? event.newValue : 'light', { persist: false });
});

const handleSystemPreferenceChange = () => {
  if (document.documentElement.dataset.colorPreference === 'system') {
    applyPreference('system', { persist: false });
  }
};

if (typeof systemPreference.addEventListener === 'function') {
  systemPreference.addEventListener('change', handleSystemPreferenceChange);
} else {
  systemPreference.addListener(handleSystemPreferenceChange);
}

applyPreference(document.documentElement.dataset.colorPreference || getStoredPreference(), {
  persist: false,
  announce: false,
});

requestAnimationFrame(() => document.documentElement.classList.add('theme-transition-ready'));
